import { hasCourseAccess } from '../../../utils/premiumCourseAccess';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTitleAndUser } from '../../../hooks/userTitle';
import { useNewTheme } from '../../../hooks/useNewTheme';
import { userRoutes } from '../../../utils/RoutesApi';
import { Content, TutoriaisContainer, PageIntro, TutorialPlayer, LibraryHeading } from '../Tutoriais/tutorialLayout';

const lessons = [
  { title: 'Aula 1: Como não perder tempo e dinheiro nos investimentos', videoId: '59844132-b799-47ac-94eb-ae907bb36f76' },
  { title: 'Aula 2: Como montar uma carteira de investimentos na prática', videoId: '7020b2dc-ccf8-4423-8b10-ceff7197b1e0' },
];

const ebooks = [
  {
    title: 'O que você precisa saber sobre Imposto de Renda nas ações e FIIs',
    url: 'https://drive.google.com/file/d/11tFoIQ4IYfsy2IK9NrQOjGOrm2OwGPaU/view?usp=sharing',
  },
  {
    title: 'Como investir em ações começando do zero',
    url: 'https://drive.google.com/file/d/1gU9VA5_HJuQ5e8Le64uWlO5H2uM9mPV6/view?usp=sharing',
  },
];

const BonusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  article {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
    border: 1px solid #edd9bb;
    border-radius: 16px;
    background: #fffbf5;
  }

  .ebook-label {
    color: #945013;
    background: #fff1df;
    border-radius: 20px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
  }

  h3 {
    color: #1c2d38;
    font-size: 17px;
    line-height: 1.6;
    margin: 16px 0 24px;
  }

  a {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #fff;
    background: #ad452c;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover { background: #913923; }
  a:focus-visible { outline: 3px solid #1c2d38; outline-offset: 3px; }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const LessonList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  button {
    display: flex;
    flex-direction: column;
    gap: 12px;
    text-align: left;
    padding: 24px;
    border: 1px solid #dce2e8;
    border-radius: 14px;
    background: #fff;
    color: #1c2d38;
    font: inherit;
    cursor: pointer;
  }

  button[aria-pressed='true'] { border-color: #ec6643; background: #fff4ed; }
  button:focus-visible { outline: 2px solid #bc482b; outline-offset: 3px; }
  span { font-size: 12px; color: #bc482b; }
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const Notice = styled.div`
  padding: 32px;
  background: #fff;
  border: 1px solid #e4e8ed;
  border-radius: 16px;
  p { color: #687581; }
  a { display: inline-block; color: #bc482b; font-weight: 600; }
`;

export function CursosPremium() {
  const { isMobile = false } = useNewTheme() as { isMobile?: boolean };
  const { setTitle, permissions } = useTitleAndUser() as {
    setTitle: (title: string) => void;
    permissions: string[];
  };
  const [access, setAccess] = useState<'loading' | 'allowed' | 'denied' | 'error'>('loading');
  const [selected, setSelected] = useState(0);
  const lesson = lessons[selected];
  const isAdmin = permissions.includes('Admin');

  useEffect(() => { setTitle('Cursos iBox6 — Premium'); }, [setTitle]);

  useEffect(() => {
    let active = true;
    if (isAdmin) {
      setAccess('allowed');
      return;
    }
    axios.get(`${process.env.REACT_APP_API_URL}${userRoutes.STATUS_PAYMENT}`)
      .then(({ data }) => { if (active) setAccess(hasCourseAccess(data) ? 'allowed' : 'denied'); })
      .catch(() => { if (active) setAccess('error'); });
    return () => { active = false; };
  }, [isAdmin]);

  return (
    <Content $isMobile={isMobile}>
      <TutoriaisContainer>
        <PageIntro>
          <span className="eyebrow">Cursos iBox6 · Premium</span>
          <h1>Cursos exclusivos disponíveis na sua assinatura Premium</h1>
          <p>Aproveite o conteúdo do Desafio Montando a Sua Carteira de Investimentos que está incluso na sua assinatura Premium do Kit do Investidor.</p>
        </PageIntro>
        {access === 'loading' && <Notice role="status">Verificando seu acesso...</Notice>}
        {access === 'error' && <Notice role="alert">Não foi possível verificar sua assinatura. Atualize a página para tentar novamente.</Notice>}
        {access === 'denied' && (
          <Notice>
            <h2>Conteúdo exclusivo</h2>
            <p>Estas aulas estão disponíveis para quem tem assinatura Premium ou bônus ativo.</p>
            <Link to="/pagamentos">Conhecer o Premium</Link>
          </Notice>
        )}
        {access === 'allowed' && (
          <>
            <TutorialPlayer>
              <iframe
                key={lesson.videoId}
                id={`panda-${lesson.videoId}`}
                title={lesson.title}
                src={`https://player-vz-447732c4-8c0.tv.pandavideo.com.br/embed/?v=${lesson.videoId}`}
                allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
              <div className="player-caption"><span className="playing-label">Aula selecionada</span><h2>{lesson.title}</h2></div>
            </TutorialPlayer>
            <LibraryHeading><div><h2>Montando a sua carteira de investimentos</h2><p>Selecione uma aula para começar.</p></div></LibraryHeading>
            <LessonList>
              {lessons.map((item, index) => (
                <button key={item.videoId} type="button" aria-pressed={selected === index} onClick={() => setSelected(index)}>
                  <strong>{item.title}</strong>
                  <span>{selected === index ? 'Aula selecionada' : 'Assistir aula'}</span>
                </button>
              ))}
            </LessonList>
            <LibraryHeading>
              <div>
                <h2>Bônus: e-books para complementar seus estudos</h2>
                <p>Abra os materiais no Google Drive, em uma nova aba.</p>
              </div>
            </LibraryHeading>
            <BonusGrid>
              {ebooks.map((ebook) => (
                <article key={ebook.url}>
                  <span className="ebook-label">E-book · Bônus</span>
                  <h3>{ebook.title}</h3>
                  <a href={ebook.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir e-book: ${ebook.title} (nova aba)`}>
                    Abrir e-book <span aria-hidden="true">↗</span>
                  </a>
                </article>
              ))}
            </BonusGrid>
          </>
        )}
      </TutoriaisContainer>
    </Content>
  );
}
